import { useEffect } from "react";

import usePrevious from "hooks/usePrevious";

/**
 * Auto-expands a collapsible component when a tracked value changes.
 * Useful for showing new items when they're added to a list.
 *
 * @param value - The value to track for changes (e.g., array length, count)
 * @param toggle - Toggle function to expand the collapsible (will be called with false)
 *
 * @example
 * const [isCollapsed, toggle] = useToggle(true);
 * useAutoExpandOnValueChange(items.length, toggle);
 */
const useAutoExpandOnValueChange = <T>(value: T, toggle: (collapsed: boolean) => void) => {
  const previousValue = usePrevious(value);

  useEffect(() => {
    if (previousValue !== value) {
      toggle(false);
    }
  }, [value, previousValue, toggle]);
};

export default useAutoExpandOnValueChange;
